import * as yup from "yup";
import { FieldOption, FieldTypes } from "../../models/formBuilder";
import "../../utils/YupHelper";

export interface FormFieldOptionData extends FieldOption {
    index: number;
}

export interface FormFieldOptionAnswerData {
    openAnswer?: string;
    id: number;
}

export interface FormFieldData {
    css?: string;
    description?: string;
    fieldId: number;
    hint?: string;
    index: number;
    isWordsRange: boolean;
    label: string;
    max?: number | null;
    min?: number | null;
    options?: FormFieldOptionData[];
    placeholder?: string;
    required: boolean;
    typeId: number;
    value?: any;
}

export interface FormSectionData {
    description?: string | null;
    fields: FormFieldData[];
    index: number;
    title?: string | null;
}

export interface FormSubmissionData {
    formId: number;
    sections: FormSectionData[];
    submissionId?: number;
}

export const FormSubmissionSchemaFields: { [field in keyof FormSubmissionData]: any } = {
    formId: yup.number().nullable(true).label("Form ID").min(1, "Invalid $label"),
    sections: yup.array().of(yup.object<FormSectionData>().shape({
        description: yup.string().nullable(true),
        fields: yup.array().of(yup.object<FormFieldData>().shape({
            css: yup.string().nullable(true),
            description: yup.string().nullable(true),
            fieldId: yup.number().label("Field ID").min(1, "Invalid $label"),
            hint: yup.string().nullable(true),
            index: yup.number(),
            isWordsRange: yup.bool(),
            label: yup.string(),
            max: yup.number().nullable(true),
            min: yup.number().nullable(true),
            options: yup.array().of(yup.object<FormFieldOptionData>().shape({
                description: yup.string().nullable(true),
                index: yup.number(),
                isOpenAnswer: yup.bool(),
                label: yup.string(),
                optionId: yup.number().label("Field option ID").min(1, "Invalid $label"),
                openAnswer: yup.string().nullable(true),
                openAnswerHint: yup.string().nullable(true),
                openAnswerPlaceholder: yup.string().nullable(true),
                selected: yup.boolean(),
            })),
            placeholder: yup.string().nullable(true),
            required: yup.bool(),
            typeId: yup.number().label("Field type ID").min(1, "Invalid $label"),
            value: yup.object().when(["typeId", "label", "required", "isWordsRange", "max", "min"],
                (typeId: number, label: string, required: boolean, isWordsRange: boolean, max?: number, min?: number) => {
                let fieldRules: any = yup.mixed().nullable(true);

                switch (typeId) {
                    case FieldTypes.Text.id:
                    case FieldTypes.MultilineText.id:
                    case FieldTypes.RichText.id:
                        fieldRules = yup.string().nullable(true);
                        break;
                    case FieldTypes.EmailAddress.id:
                        fieldRules = yup.string().email().nullable(true);
                        break;
                    case FieldTypes.Number.id:
                    case FieldTypes.Integer.id:
                        fieldRules = yup.number().nullable(true);
                        break;
                    case FieldTypes.CheckList.id:
                    case FieldTypes.Dropdown.id:
                    case FieldTypes.RadioList.id:
                        fieldRules = yup.mixed().nullable(true);
                        break;
                    case FieldTypes.Date.id:
                    case FieldTypes.DateTime.id:
                        fieldRules = yup.date().nullable(true);
                        break;
                    case FieldTypes.TimeSpan.id:
                    case FieldTypes.TimeSpanMinutes.id:
                        fieldRules = yup.string().nullable(true);
                        break;
                }

                if (required && typeId !== undefined && !FieldTypes.isListType(typeId))
                    fieldRules = fieldRules.required();

                if (max !== undefined && max !== null && !isNaN(max)) {
                    if (typeId === FieldTypes.RichText.id && isWordsRange)
                        fieldRules = (fieldRules as any).wordcount_max(max);
                    else if (typeId === FieldTypes.RichText.id && !isWordsRange)
                        fieldRules = (fieldRules as any).richtextchar_max(max);
                    else if (typeId === FieldTypes.TimeSpan.id)
                        fieldRules = (fieldRules as any).timespan_max(max);
                    else
                        fieldRules = fieldRules.max(max);
                }
                if (min !== undefined && min !== null && !isNaN(min)) {
                    if (typeId === FieldTypes.RichText.id && isWordsRange)
                        fieldRules = (fieldRules as any).wordcount_min(min);
                    else if (typeId === FieldTypes.RichText.id && !isWordsRange)
                        fieldRules = (fieldRules as any).richtextchar_min(min);
                    else if (typeId === FieldTypes.TimeSpan.id)
                        fieldRules = (fieldRules as any).timespan_min(max);
                    else
                        fieldRules = fieldRules.min(min);
                }

                return fieldRules.label(label);
            }),
        })),
        index: yup.number(),
        title: yup.string().nullable(true),
    })),
    submissionId: yup.number().nullable(true).label("Form submission ID").min(0, "Invalid $label"),
};

export const FormSubmissionSchema = yup.object<FormSubmissionData>().shape({
    ...FormSubmissionSchemaFields,
});

export const DefaultFormSubmissionData: FormSubmissionData = {
    sections: [],
    formId: 0,
};



// WEBPACK FOOTER //
// ./src/forms/formBuilder/FormSubmission.ts